import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as TableIcon } from '@elementary/common-ui/assets/16x16/table.svg';
import { ReactComponent as ColumnIcon } from '@elementary/common-ui/assets/columns.svg';
import {
  Flex,
  FlexColumn,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import Table, {
  Column,
  TableEmptyState,
  TableFilters,
  ToggleColumnsMenu,
} from '@elementary/common-ui/components/Table';
import { Operator } from '@elementary/common-ui/hooks/useUrlFilters';
import { distinctArray } from '@elementary/common-ui/utils/common';
import { Divider, Typography } from '@mui/material';
import { Control, ControllerRenderProps, useController } from 'react-hook-form';
import useDebounce from '@elementary/common-ui/hooks/useDebounce';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import {
  ADD_TEST_RESOURCES_TABLE_FILTERS,
  ADD_TEST_RESOURCES_TABLE_SELECT_ALL,
} from '@elementary/common-ui/constants/urlParams';
import { AddTestForm, ColumnResource, Resource } from 'shared/types';
import { radius, space } from '@elementary/common-ui/style/utils';
import ColumnTagsExpanded from './WizardTargetStage/ColumnTagsExpanded';

const CellCenteredWrapper = styled(Flex)`
  height: 100%;
  align-items: start;
  padding-top: ${space('lg')};
`;

const AssetNameWrapper = styled(Flex)`
  overflow: hidden;
  align-items: center;
  border-radius: ${radius('lg')};
  gap: ${space('xs')};
  min-width: 50px;
`;

const TableIconStyled = styled(TableIcon)`
  width: 17px;
  min-width: 17px;
`;

const ColumnIconStyled = styled(ColumnIcon)`
  width: 17px;
  min-width: 17px;
`;

const TableHeader = styled(Flex)`
  gap: ${space('xxl')};
  padding: ${space(0, 'xxl')};
  justify-content: space-between;
  align-items: flex-start;
`;

const tableColumnFilterCompare = (
  selectedFilterValue: string[],
  rowData: TableResource | ColumnResource,
  operator: Operator,
) => {
  const tableRow = rowData as TableResource;
  const filterValue = selectedFilterValue[0];
  if (operator === 'contains' || operator === 'equals') {
    return tableRow.columns
      .flat()
      .map((col) => col.name)
      .includes(filterValue);
  }
  if (operator === 'notContains' || operator === 'notEquals') {
    return !tableRow.columns
      .flat()
      .map((col) => col.name)
      .includes(filterValue);
  }
  return false;
};

const tableColumnTypesFilterCompare = (
  selectedFilterValue: string[],
  rowData: TableResource | ColumnResource,
  operator: Operator,
) => {
  const tableRow = rowData as TableResource;
  const filterValue = selectedFilterValue[0];
  if (operator === 'contains' || operator === 'equals') {
    return tableRow.columns_types.includes(filterValue);
  }
  if (operator === 'notContains' || operator === 'notEquals') {
    return !tableRow.columns_types.includes(filterValue);
  }
  return false;
};

type TableResource = Resource & {
  columns_types: string[];
};

const getDisplayNameColumnValue = (
  val: TableResource | ColumnResource,
  resourceTypeField: ControllerRenderProps<AddTestForm, 'resourceType'>,
) => {
  const prefix = val.source_name ? 'source' : 'model';
  const schema = val.schema?.toLowerCase() || '';
  const modelName =
    resourceTypeField.value === 'columns' && (val as ColumnResource).model_name
      ? `${(val as ColumnResource).model_name.toLowerCase()}.`
      : '';
  const name = val.name?.toLowerCase() || '';

  return `${prefix}.${schema}.${modelName}${name}`;
};

const nameColumnRenderer = (
  val: TableResource | ColumnResource,
  resourceTypeField: ControllerRenderProps<AddTestForm, 'resourceType'>,
) => {
  const isDbt = val.source_type?.toLowerCase() === 'dbt';
  const isColumn = resourceTypeField.value === 'columns';

  let prefix: string;
  let name: string;

  if (isDbt) {
    const displayName = getDisplayNameColumnValue(val, resourceTypeField);
    const lastDot = displayName.lastIndexOf('.');
    prefix = lastDot >= 0 ? displayName.substring(0, lastDot + 1) : '';
    name = lastDot >= 0 ? displayName.substring(lastDot + 1) : displayName;
  } else {
    prefix = isColumn
      ? (val as ColumnResource).table_fqn || ''
      : (val as TableResource).fqn?.substring(
          0,
          ((val as TableResource)?.fqn || '').lastIndexOf('.') + 1,
        ) || '';
    name = isColumn
      ? `.${(val as ColumnResource).name}`
      : (val as TableResource).table_name || '';
  }

  return (
    <CellCenteredWrapper>
      <AssetNameWrapper>
        {isColumn ? <ColumnIconStyled /> : <TableIconStyled />}
        <OverflowTooltip>
          <Typography sx={{ color: 'content.secondary' }} component="span">
            {prefix}
          </Typography>
          <Typography sx={{ color: 'inherit' }} component="span">
            {name}
          </Typography>
        </OverflowTooltip>
      </AssetNameWrapper>
    </CellCenteredWrapper>
  );
};
type Props = {
  control: Control<AddTestForm, any>;
  tableResourcesData: Resource[];
  columnsResourcesData: ColumnResource[];
  isLoading?: boolean;
};

const WizardTargetStage = ({
  control,
  tableResourcesData,
  columnsResourcesData,
  isLoading = false,
}: Props) => {
  const [excludedItems, setExcludedItems] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [filteredData, setFilteredData] = useState<
    (TableResource | ColumnResource)[]
  >([]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Saving this to be able to navigate to this table with select all by default
  const isSelectAll: boolean =
    searchParams.get(ADD_TEST_RESOURCES_TABLE_SELECT_ALL) === 'true';
  const setIsSelectAll = useCallback(
    (val: boolean) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        if (val) {
          newParams.set(ADD_TEST_RESOURCES_TABLE_SELECT_ALL, 'true');
        } else {
          newParams.delete(ADD_TEST_RESOURCES_TABLE_SELECT_ALL);
        }
        return newParams;
      });
    },
    [setSearchParams],
  );

  const { field: resourcesField } = useController({
    name: 'resources',
    control,
    rules: { required: true },
  });

  const { field: resourceTypeField } = useController({
    name: 'resourceType',
    control,
    defaultValue: 'tables',
  });

  const selectedItems = resourcesField.value ?? [];
  const prevResourceTypeRef = useRef(resourceTypeField.value);

  useEffect(() => {
    if (!isSelectAll) {
      return;
    }
    if (excludedItems.length) {
      resourcesField.onChange(
        filteredData
          .filter((item) => !excludedItems.includes(item.id))
          .map((item) => item.id),
      );
      return;
    }
    resourcesField.onChange(filteredData.map((item) => item.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [excludedItems, isSelectAll, filteredData]);

  useEffect(() => {
    if (prevResourceTypeRef.current === resourceTypeField.value) {
      return;
    }
    prevResourceTypeRef.current = resourceTypeField.value;
    setExcludedItems([]);
    resourcesField.onChange([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceTypeField.value]);

  const columnsResourcesDataFormatted: ColumnResource[] = useMemo(
    () =>
      columnsResourcesData.map((item) => ({
        ...item,
        display_name: getDisplayNameColumnValue(
          item as ColumnResource,
          resourceTypeField,
        ),
        table_name: item.model_name,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resourceTypeField.value, columnsResourcesData],
  );

  const tableResourcesDataFormatted: TableResource[] = useMemo(
    () =>
      tableResourcesData.map((item) => ({
        ...item,
        display_name: getDisplayNameColumnValue(
          item as TableResource,
          resourceTypeField,
        ),
        table_name: item.table_name,
        columns: item.columns.sort(({ type: typeA }, { type: typeB }) =>
          typeA > typeB ? -1 : 1,
        ),
        columns_types: distinctArray(
          item.columns.map((col) => col.type).flat(),
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resourceTypeField.value, tableResourcesData],
  );

  const columns: Array<Column<TableResource | ColumnResource>> = useMemo(() => {
    const tableColumnsFilterOverride = distinctArray(
      tableResourcesDataFormatted
        .map((res) => res.columns)
        .flat()
        .map((col) => col.name),
    ).map((col) => ({ label: col, value: col }));

    const columnTypesFilterOverride = distinctArray(
      tableResourcesDataFormatted.map((res) => res.columns_types).flat(),
    ).map((type) => ({ label: type, value: type }));

    return [
      {
        id: 'display_name',
        header: 'Asset',
        hideColumnFilter: true,
        renderer: (val) => nameColumnRenderer(val, resourceTypeField),
        filterValueMatcher: (selectedFilterValue, rowData, operator) => {
          const rowValue = getDisplayNameColumnValue(
            rowData,
            resourceTypeField,
          ).toLowerCase();
          const filterValue = selectedFilterValue[0].toLowerCase();
          if (operator === 'contains' || operator === 'equals') {
            return rowValue.includes(filterValue);
          }
          if (operator === 'notContains' || operator === 'notEquals') {
            return !rowValue.includes(filterValue);
          }
          return false;
        },
        filterOptionsOverride:
          resourceTypeField.value === 'tables'
            ? undefined
            : distinctArray(
                columnsResourcesDataFormatted
                  .map((res) => {
                    const parts = res.display_name?.split('.');
                    return parts?.[parts.length - 1];
                  })
                  .filter((col): col is string => col !== undefined),
              ).map((name) => ({ label: name, value: name })),
      },
      {
        id: 'db_name',
        header: 'Database',
        filterable: true,
        width: 150,
      },
      {
        id: 'schema',
        header: 'Schema',
        filterable: true,
        width: 200,
      },
      {
        id: 'table_name',
        header: 'Table',
        filterable: true,
        width: 200,
      },
      ...(resourceTypeField.value === 'columns'
        ? [
            {
              id: 'name',
              header: 'Column',
              filterable: true,
              width: 200,
            },
          ]
        : []),
      {
        id: 'owners',
        header: 'Owners',
        hideColumn: true,
        width: 200,
      },
      {
        id: 'tags',
        header: 'Tags',
        hideColumn: true,
        width: 200,
      },
      ...(resourceTypeField.value === 'columns'
        ? [
            {
              id: 'type',
              header: 'Type',
              width: 150,
              renderer: (val: TableResource | ColumnResource) => (
                <OverflowTooltip>
                  {(val as ColumnResource)?.type?.toUpperCase()}
                </OverflowTooltip>
              ),
            },
          ]
        : []),
      ...(resourceTypeField.value === 'tables'
        ? [
            {
              id: 'materialization',
              header: 'Materialization',
              filterable: true,
              width: 250,
              renderer: (val: TableResource | ColumnResource) => (
                <OverflowTooltip>
                  {'materialization' in val &&
                    val.source_type?.toLowerCase() === 'dbt' &&
                    val.materialization}
                </OverflowTooltip>
              ),
            },
            {
              id: 'table_type',
              header: 'Table type',
              filterable: true,
              width: 150,
            },
            {
              id: 'source_type',
              header: 'Source type',
              filterable: true,
              width: 150,
            },
            {
              id: 'columns',
              header: 'Columns',
              width: 450,
              filterOptionsOverride: tableColumnsFilterOverride,
              filterValueMatcher: tableColumnFilterCompare,
              renderer: (val: TableResource | ColumnResource) => (
                <ColumnTagsExpanded
                  columns={'columns' in val ? val.columns : []}
                />
              ),
            },
            {
              id: 'columns_types',
              header: 'Columns types',
              width: 200,
              defaultUntoggled: true,
              filterOptionsOverride: columnTypesFilterOverride,
              filterValueMatcher: tableColumnTypesFilterCompare,
              renderer: (val: TableResource | ColumnResource) => (
                <OverflowTooltip>
                  {'columns_types' in val && val.columns_types.join(', ')}
                </OverflowTooltip>
              ),
            },
          ]
        : [
            {
              id: 'model_name',
              header: 'Model name',
              width: 200,
              defaultUntoggled: true,
            },
          ]),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    columnsResourcesDataFormatted,
    resourceTypeField.value,
    tableResourcesDataFormatted,
  ]);

  return (
    <FlexColumn sx={{ gap: 2, height: '100%', pt: 2 }}>
      <TableHeader>
        <TableFilters
          data={
            resourceTypeField.value === 'tables'
              ? tableResourcesDataFormatted
              : columnsResourcesDataFormatted
          }
          columns={columns}
          urlParamsPrefix={ADD_TEST_RESOURCES_TABLE_FILTERS}
          search={search}
          onSearch={(val) => setSearch(val)}
        />
        <ToggleColumnsMenu
          columns={columns}
          localStorageKey={`${ADD_TEST_RESOURCES_TABLE_FILTERS}_table`}
        />
      </TableHeader>
      <Divider />

      <Table
        sx={{ px: 3 }}
        data={
          resourceTypeField.value === 'tables'
            ? tableResourcesDataFormatted
            : columnsResourcesDataFormatted
        }
        columns={columns}
        selectionColumn
        selectedItems={selectedItems}
        scrollSelectionIntoView
        onSelectItem={(items) => resourcesField.onChange(items)}
        isSelectAll={isSelectAll}
        onSelectAllChange={setIsSelectAll}
        excludedItems={excludedItems}
        onExcludeItem={(items) => setExcludedItems(items)}
        bottomScrollPadding={24}
        search={debouncedSearch}
        onFilteredItemsChange={(data) => {
          setFilteredData((prev) => {
            if (
              prev.length === data.length &&
              prev.every((item, idx) => item === data[idx])
            ) {
              return prev;
            }
            return data;
          });
        }}
        localStorageKey={`${ADD_TEST_RESOURCES_TABLE_FILTERS}_table`}
        urlParamsPrefix={ADD_TEST_RESOURCES_TABLE_FILTERS}
        emptyStateRenderer={() => <TableEmptyState />}
        isLoading={isLoading}
      />
    </FlexColumn>
  );
};

export default WizardTargetStage;
